import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Monitor, Smartphone, LogOut, Pencil, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toUtc } from "@/lib/mood";
import { logError } from "@/lib/log";

const isPhone = (label) => /iPhone|iPad|Android/.test(label);
const ago = (d) => {
  const mins = Math.round((Date.now() - new Date(toUtc(d)).getTime()) / 60000);
  if (mins < 2) return "Active now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return new Date(toUtc(d)).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const DeviceName = ({ d, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(d.nickname || "");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/devices/${d.id}/rename`, { nickname: val.trim() });
      toast.success(val.trim() ? "Device renamed" : "Nickname removed");
      setEditing(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (editing) {
    return (
      <form onSubmit={save} className="flex items-center gap-1" data-testid={`device-rename-form-${d.id}`}>
        <input autoFocus className="mm-input !py-1 !text-sm" maxLength={40} value={val} placeholder={d.device_name}
          onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setEditing(false)} data-testid={`device-rename-input-${d.id}`} />
        <button className="p-1.5 text-mm-success" aria-label="Save" data-testid={`device-rename-save-${d.id}`}><Check size={16} /></button>
        <button type="button" className="p-1.5 text-mm-muted" aria-label="Cancel" onClick={() => setEditing(false)} data-testid={`device-rename-cancel-${d.id}`}><X size={16} /></button>
      </form>
    );
  }
  return (
    <div className="font-medium text-sm flex items-center gap-1.5 flex-wrap">
      <span data-testid={`device-label-${d.id}`}>{d.label}</span>
      <button onClick={() => { setVal(d.nickname || ""); setEditing(true); }} className="text-mm-muted hover:text-mm-primary" aria-label="Rename device" data-testid={`device-rename-${d.id}`}><Pencil size={13} /></button>
      {d.current && <span className="text-xs text-mm-success font-bold" data-testid="device-current-badge">This device</span>}
    </div>
  );
};

export const DevicesCard = () => {
  const [devices, setDevices] = useState(null);
  const { logout } = useAuth();
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/devices").then((r) => setDevices(r.data)).catch((e) => logError("Devices load failed:", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const signOut = async (d) => {
    try {
      if (d.current) { await logout(); nav("/landing"); return; }
      await api.delete(`/devices/${d.id}`);
      toast.success(`Signed out ${d.label}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const signOutOthers = async () => {
    try { const { data } = await api.post("/devices/sign-out-others"); toast.success(`Signed out ${data.signed_out} other device(s)`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!devices) return null;
  const others = devices.filter((d) => !d.current).length;
  return (
    <div className="mm-card mt-3 overflow-hidden" data-testid="devices-card">
      <div className="p-5 pb-3 flex justify-between items-center gap-3">
        <div className="font-semibold">Signed-in devices ({devices.length})</div>
        {others > 0 && <button className="text-sm text-mm-error font-semibold" onClick={signOutOthers} data-testid="devices-sign-out-others">Sign out all others</button>}
      </div>
      <div className="divide-y">
        {devices.map((d) => {
          const I = isPhone(d.device_name) ? Smartphone : Monitor;
          return (
            <div key={d.id} className="px-5 py-3 flex items-center gap-3" data-testid={`device-${d.id}`}>
              <span className="w-9 h-9 rounded-xl mm-soft flex items-center justify-center"><I size={18} /></span>
              <div className="flex-1 min-w-0">
                <DeviceName d={d} onSaved={load} />
                <div className="text-xs text-mm-muted">{d.nickname ? `${d.device_name} · ` : ""}{d.ip} · {ago(d.last_seen)}</div>
              </div>
              <button onClick={() => signOut(d)} className="text-xs font-semibold text-mm-muted hover:text-mm-error flex items-center gap-1" data-testid={`device-sign-out-${d.id}`}>
                <LogOut size={14} />Sign out
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
